/**
 * Build sandboxed iframe `srcDoc` for HTML-mode custom_code previews.
 * Matches public Hybrid semantics: CSS in a style tag, JS with `container` + `document`.
 */

import {
	collectGoogleFontFamiliesFromCss,
	type GoogleFontFamilyRequest,
	googleFontsStylesheetHref,
	mergeGoogleFontFamilyRequests,
} from "../theme/googleFonts";
import {
	CUSTOM_CODE_FORM_SLOT_ATTR,
	CUSTOM_CODE_FORM_SLOT_NAME_ATTR,
} from "./customCodeFormSlotRefs";
import { normalizeHtmlCustomCodeParts } from "./customCodeHtmlRuntime";
import {
	buildCustomCodeIframeThemeParts,
	type CustomCodeIframeColorMode,
	type CustomCodeIframeStyles,
} from "./customCodeIframeTheme";

export const HTML_CUSTOM_CODE_IFRAME_ROOT_ID = "cc-html-root";

/** Preview-only box for `[data-cms-form]`; the real form mounts on the public page. */
const FORM_SLOT_PREVIEW_CSS = `[${CUSTOM_CODE_FORM_SLOT_ATTR}] { display: flex; align-items: center; justify-content: center; min-height: 120px; box-sizing: border-box; padding: 16px; border: 1px dashed #9ca3af; border-radius: 8px; background: rgba(156, 163, 175, 0.08); color: #6b7280; font: 13px/1.4 system-ui, sans-serif; text-align: center; }
    [${CUSTOM_CODE_FORM_SLOT_ATTR}]::before { content: "Form: " attr(${CUSTOM_CODE_FORM_SLOT_ATTR}) " (renders on the published page)"; }
    [${CUSTOM_CODE_FORM_SLOT_ATTR}][${CUSTOM_CODE_FORM_SLOT_NAME_ATTR}]::before { content: "Form: " attr(${CUSTOM_CODE_FORM_SLOT_NAME_ATTR}) " (renders on the published page)"; }
    [${CUSTOM_CODE_FORM_SLOT_ATTR}=""]::before { content: "Form: choose a form in the sidebar"; }`;

/** Prevent `</style>` in tenant CSS from terminating the preview style tag. */
export function escapeCssForStyleTag(css: string): string {
	return css.replace(/<\/style/gi, "<\\/style");
}

export function buildCanvasCustomCodeCss(options: {
	pageHead?: string;
	pageBody?: string;
	element?: string;
	pageFooter?: string;
}): string {
	return [
		options.pageHead,
		options.pageBody,
		options.element,
		options.pageFooter,
	]
		.map((css) => css?.trim() ?? "")
		.filter(Boolean)
		.map(escapeCssForStyleTag)
		.join("\n");
}

/**
 * Run tenant JS the same way as {@link executeHtmlCustomJs}:
 * `new Function("container", "document", js)(root, document)`.
 */
export function buildHtmlCustomCodeIframeJsBootstrap(
	js: string,
	rootId: string = HTML_CUSTOM_CODE_IFRAME_ROOT_ID
): string {
	const trimmed = js.trim();
	if (!trimmed) {
		return "";
	}
	const rootIdJson = JSON.stringify(rootId);
	const jsJson = JSON.stringify(trimmed);
	return `(function(){
  var root = document.getElementById(${rootIdJson});
  if (!root) return;
  try {
    (new Function("container", "document", ${jsJson}))(root, document);
  } catch (e) {
    console.error("Error executing custom JS:", e);
  }
})();`;
}

/** Preconnect + one stylesheet link per family. Empty when nothing to load. */
export function buildGoogleFontsIframeHeadHtml(
	families: readonly GoogleFontFamilyRequest[]
): string {
	if (families.length === 0) {
		return "";
	}
	const links = families.map(
		(entry) =>
			`<link rel="stylesheet" href="${googleFontsStylesheetHref(entry)}"/>`
	);
	return [
		'<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin="anonymous"/>',
		...links,
	].join("\n  ");
}

export function googleFontFamiliesForCustomCodeIframe(options: {
	css?: string;
	pageCss?: string;
	themeGoogleFonts?: readonly GoogleFontFamilyRequest[];
}): GoogleFontFamilyRequest[] {
	return mergeGoogleFontFamilyRequests([
		options.themeGoogleFonts ?? [],
		collectGoogleFontFamiliesFromCss(
			[options.pageCss ?? "", options.css ?? ""].filter(Boolean).join("\n")
		),
	]);
}

export type BuildHtmlCustomCodeIframeSrcDocOptions = {
	html?: string;
	css?: string;
	js?: string;
	/** Extra script bodies appended after tenant JS (height ping, inline edit, etc.). */
	extraBodyScripts?: string[];
	rootId?: string;
	/** Builder theme + page CSS; omitted → unthemed document, as before. */
	iframeStyles?: CustomCodeIframeStyles | null;
	colorMode?: CustomCodeIframeColorMode;
};

/**
 * Full HTML document for canvas / source previews.
 * Normalizes document/fragment markup so styles/scripts in the HTML tab are applied.
 */
export function buildHtmlCustomCodeIframeSrcDoc(
	options: BuildHtmlCustomCodeIframeSrcDocOptions
): string {
	const rootId = options.rootId ?? HTML_CUSTOM_CODE_IFRAME_ROOT_ID;
	const normalized = normalizeHtmlCustomCodeParts({
		html: options.html ?? "",
		css: options.css ?? "",
		js: options.js ?? "",
	});
	const css = escapeCssForStyleTag(normalized.css);
	const jsBootstrap = buildHtmlCustomCodeIframeJsBootstrap(
		normalized.js,
		rootId
	);
	const extra = (options.extraBodyScripts ?? [])
		.map((body) => body.trim())
		.filter(Boolean)
		.map((body) => `<script>${body}</script>`)
		.join("\n  ");
	const theme = buildCustomCodeIframeThemeParts(
		options.iframeStyles,
		options.colorMode
	);
	const fontHead = buildGoogleFontsIframeHeadHtml(
		googleFontFamiliesForCustomCodeIframe({
			css,
			pageCss: options.iframeStyles?.pageCss,
			themeGoogleFonts: options.iframeStyles?.themeGoogleFonts,
		})
	);

	return `<!DOCTYPE html>
<html${theme.htmlAttrs}>
<head>
  <meta charset="utf-8"/>${fontHead ? `\n  ${fontHead}` : ""}${theme.headStyles ? `\n  ${theme.headStyles}` : ""}
  <style>
    body { margin: 0; padding: 0; font-family: system-ui, sans-serif; }
    ${css}${normalized.html.includes(CUSTOM_CODE_FORM_SLOT_ATTR) ? `\n    ${FORM_SLOT_PREVIEW_CSS}` : ""}
  </style>
</head>
<body${theme.bodyAttrs}>
  <div id="${rootId}"${theme.rootAttrs}>${normalized.html}</div>
  ${jsBootstrap ? `<script>${jsBootstrap}</script>` : ""}
  ${extra}
</body>
</html>`;
}
